import type { StockAvailability } from "@lib/analytics/analyticsTypes";
import type { Variant } from "@lib/wrappers/algolia/types";

const OUT_OF_STOCK_LABEL = "Out of Stock";

// Labels are part of the dataLayer contract, do not rename.
const STOCK_COUNT_BUCKETS = [
    { max: 5, label: "0-5" },
    { max: 10, label: "5-10" },
    { max: 20, label: "10-20" },
    { max: 30, label: "20-30" },
    { max: 50, label: "30-50" },
    { max: Infinity, label: "Over 50" },
];

const getStockCountLabel = (quantity: number): string => {
    if (!(quantity > 0)) {
        return OUT_OF_STOCK_LABEL;
    }

    return (
        STOCK_COUNT_BUCKETS.find((bucket) => quantity <= bucket.max)?.label ?? OUT_OF_STOCK_LABEL
    );
};

/** A size resolves to exactly one list, in stock or out of stock, and appears only once. */
export const getStockAvailability = (variants?: Variant[]): StockAvailability | undefined => {
    if (!variants?.length) {
        return undefined;
    }

    const countedSkus = new Set<string>();
    const sizes = new Map<string, { quantity: number; hasStock: boolean }>();

    variants.forEach(({ size, sku, hasStock, availableQuantity }) => {
        if (!size) {
            return;
        }

        // The same sku can be indexed more than once, counting it twice inflates the quantity.
        if (sku) {
            if (countedSkus.has(sku)) {
                return;
            }
            countedSkus.add(sku);
        }

        const current = sizes.get(size);

        sizes.set(size, {
            quantity: (current?.quantity ?? 0) + (availableQuantity ?? 0),
            hasStock: (current?.hasStock ?? false) || Boolean(hasStock),
        });
    });

    if (!sizes.size) {
        return undefined;
    }

    const availability: StockAvailability = {
        stock_count_online: {},
        in_stock_size: [],
        out_of_stock_size: [],
    };

    sizes.forEach(({ quantity, hasStock }, size) => {
        availability.stock_count_online[size] = hasStock
            ? getStockCountLabel(quantity)
            : OUT_OF_STOCK_LABEL;

        if (hasStock) {
            availability.in_stock_size.push(size);
        } else {
            availability.out_of_stock_size.push(size);
        }
    });

    return availability;
};
